import useStores from 'shared/hooks/use-stores';
import { dollarsToCents } from 'shared/helpers/utils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useQuery } from '@apollo/client';

import { useCheckoutLineItems } from 'src/checkout/hooks/use-checkout-line-items';
import { GetUserIncentiveEligibilityDocument } from 'src/gql/graphql';
import { ValidIncentiveValue, isValidIncentiveValue } from '../shared/incentive-types';

export type UseCashBackIncentivesOptions = {
  dispensaryId?: string;
  totalAmount?: number;
};

export type CashBackIncentiveData = {
  eligible: boolean;
  incentive?: {
    id: string;
    name: string;
    description?: string | null;
    amountCents: number;
    cohortName: string;
    active: boolean;
  } | null;
};

export function useCashBackIncentives(
  options?: UseCashBackIncentivesOptions
): {
  incentiveData: CashBackIncentiveData | null;
  loading: boolean;
  error: any;
  shouldShowBanner: boolean;
  cashBackIncentiveValue: ValidIncentiveValue | undefined;
} {
  const { Cart, User } = useStores();
  const flags = useFlags();
  const checkoutLineItems = useCheckoutLineItems();

  const cashBackRedemptionEnabled = flags['fintech.incentives.redemption.fe.rollout'] ?? false;
  const cartTotal = checkoutLineItems.find((lineItem) => lineItem.type === 'total')?.value;
  const dispensaryId = options === undefined ? Cart.order.dispensary?.id : options.dispensaryId;
  const totalAmount = options === undefined ? cartTotal : options.totalAmount;
  const totalAmountCents =
    totalAmount !== undefined && Number.isFinite(totalAmount) && totalAmount > 0
      ? dollarsToCents(totalAmount, true)
      : undefined;

  const shouldSkip = !cashBackRedemptionEnabled || !User.exists || !User.id || !dispensaryId;

  const { data, loading, error } = useQuery(GetUserIncentiveEligibilityDocument, {
    skip: shouldSkip,
    variables: {
      externalId: User.id,
      dispensaryId,
      ...(totalAmountCents !== undefined && { totalAmountCents }),
    },
    fetchPolicy: 'cache-first',
    errorPolicy: 'ignore',
  });

  const incentiveData = cashBackRedemptionEnabled ? data?.getUserIncentiveEligibility ?? null : null;
  const amountCents = incentiveData?.incentive?.amountCents;

  // Convert amountCents to ValidIncentiveValue (dollars) - only if valid
  const calculatedValue = amountCents !== undefined ? Math.round(amountCents / 100) : undefined;
  const cashBackIncentiveValue = isValidIncentiveValue(calculatedValue) ? calculatedValue : undefined;

  // Only show banner if the converted value is valid to prevent showing banners with undefined incentive values
  const shouldShowBanner = Boolean(
    cashBackRedemptionEnabled && incentiveData?.eligible && cashBackIncentiveValue !== undefined
  );

  return {
    incentiveData,
    loading,
    error,
    shouldShowBanner,
    cashBackIncentiveValue,
  };
}
